// src/components/CourseDashboard.js — calm, three-layered course landing
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Play, Clock, BookOpen, Terminal, ArrowRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { apiService, handleApiError } from '../services/api';
import { formatDuration } from '../utils/helpers';
import Loading from './common/Loading';
import usePageTitle from '../hooks/usePageTitle';
import { Button, Card, Breadcrumbs } from './ui';
import { DomainCard as FNDomainCard } from './ui/dashboard';
import { useTutor } from '../contexts/TutorRuntimeContext';

export default function CourseDashboard() {
  const { courseId } = useParams();
  const [courseData, setCourseData] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [masteryRows, setMasteryRows] = useState([]);
  const { sendToTutor } = useTutor();
  useEffect(() => {
    let alive = true;
    apiService.getMasteryDashboard(courseId)
      .then((res) => {
        if (!alive) return;
        const d = res?.data;
        setMasteryRows(Array.isArray(d) ? d : d?.concepts || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [courseId]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [courseResponse, progressResponse] = await Promise.all([
          apiService.getCourse(courseId),
          apiService.getProgressOverview(courseId).catch(() => ({ data: { data: { is_new_user: true } } }))
        ]);
        setCourseData(courseResponse.data);
        setProgressData(progressResponse.data.data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [courseId]);

  usePageTitle(courseData?.name || 'Course');

  if (loading) return <Loading message="Loading course..." />;
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-muted">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-read">{error}</p>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </div>
    );
  }

  const isNewUser = progressData?.is_new_user;

  // Top-fold stat strip — only show stats that have meaningful values.
  const topStats = [];
  topStats.push({ label: 'domains', value: courseData?.domains?.length || 0 });
  if (!isNewUser && progressData?.total_questions) {
    topStats.push({ label: 'questions answered', value: progressData.total_questions });
  }
  if (!isNewUser && progressData?.total_time_minutes) {
    topStats.push({ label: 'total time', value: formatDuration(progressData.total_time_minutes) });
  }
  if (!isNewUser && progressData?.overall_success_rate != null) {
    topStats.push({ label: 'success rate', value: `${progressData.overall_success_rate}%` });
  }

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-5">

        {/* Pass 1 — overview: title + description + inline stat strip.
            Tighter than before — the user complained the description
            ate half the screen and the domains landed below the fold. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-1.5"
        >
          <Breadcrumbs items={[
            { label: 'My Courses', to: '/my-courses' },
            { label: courseData?.name },
          ]} />
          {courseData?.description && (
            <p className="text-sm text-mutedink max-w-2xl line-clamp-2">{courseData.description}</p>
          )}
          {topStats.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs">
              {topStats.map((s, i) => (
                <span key={s.label} className="text-mutedink">
                  <span className="text-ink font-medium tabular-nums">{s.value}</span>
                  {' '}{s.label}
                  {i < topStats.length - 1 && <span className="text-faint ml-4">·</span>}
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Pass 2 — the one primary action. Compact band, single line. */}
        <Link
          to={`/course/${courseId}/practice`}
          className="group flex items-center justify-between bg-accent-soft hover:bg-accent-subtle border border-accent-subtle rounded-lg px-4 py-2.5 transition-colors"
        >
          <div className="flex items-baseline gap-3 min-w-0">
            <span className="text-sm font-semibold text-ink">
              {isNewUser ? 'Start practicing' : "Today's practice"}
            </span>
            <span className="text-xs text-mutedink truncate">
              {isNewUser
                ? 'Pick a domain — questions adapt to what you know.'
                : 'Personalized study plan with readiness score.'}
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </Link>

        {/* Pass 3 — tools row. No heading; the domain section already
            uses a small label so the second one was redundant noise. */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <ToolLink to={`/course/${courseId}/quick-quiz`} label="Quick quiz" />
          <ToolLink to={`/course/${courseId}/mastery`}    label="Mastery" />
          <ToolLink to={`/course/${courseId}/insights`}   label="Insights" />
          <ToolLink to={`/course/${courseId}/manage`}     label="Content" />
        </div>

        {/* Pass 4 — domains */}
        {courseData?.domains && courseData.domains.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-faint font-normal">Domains</h2>
            <div className="space-y-3">
              {courseData.domains.map((domain) => {
                const rows = masteryRows.filter(
                  (r) => r.domain === domain.key || r.domain === domain.name,
                );
                const dist = rows.reduce((acc, r) => {
                  acc[r.mastery_level] = (acc[r.mastery_level] || 0) + 1;
                  return acc;
                }, {});
                const due = rows.filter((r) => r.due_for_review).length;
                // Surface the weakest/due rows first; cap for page rhythm —
                // the full set lives on the Mastery page.
                const shown = [...rows].sort((a, b) => {
                  if (!!b.due_for_review !== !!a.due_for_review) return b.due_for_review ? 1 : -1;
                  return (a.mastery_score ?? 99) - (b.mastery_score ?? 99);
                }).slice(0, 5);
                return (
                  <FNDomainCard
                    key={domain.key}
                    d={{ key: domain.key, name: domain.name, description: domain.description, dist, due, rows: shown, blockers: [] }}
                    onAsk={(mrow) => sendToTutor(`Help me with ${mrow.concept} in ${courseId} — where should I start?`)}
                    footer={
                      <div className="dc-actions">
                        <Button to={`/course/${courseId}/quiz/${domain.key}`} variant="primary" size="sm">
                          <Play className="w-3.5 h-3.5" /> Quiz
                        </Button>
                        <Button to={`/course/${courseId}/content/${domain.key}`} variant="secondary" size="sm" aria-label="Content">
                          <BookOpen className="w-3.5 h-3.5" />
                        </Button>
                        {rows.length > shown.length && (
                          <span className="ml-auto self-center font-mono text-[10px] text-mutedink">
                            +{rows.length - shown.length} more on Mastery
                          </span>
                        )}
                      </div>
                    }
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Tool tile — neutral surface, single accent on hover.
function ToolLink({ to, label }) {
  return (
    <Link
      to={to}
      className="block bg-surface border border-line hover:border-line-strong rounded-lg px-4 py-3 text-sm text-read hover:text-ink transition-colors text-center"
    >
      {label}
    </Link>
  );
}

// One domain = one Card row. Stats inline if available, no decoration if not.
function DomainCard({ courseId, domain, progress }) {
  const hasProgress = progress && progress.attempts > 0;
  const successPct = hasProgress ? Math.min(progress.success_rate, 100) : 0;

  return (
    <Card className="p-5 flex flex-col justify-between">
      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <h3 className="font-medium text-ink truncate">{domain.name}</h3>
          <span className="text-xs text-faint tabular-nums flex-shrink-0">
            {domain.weight_percentage || Math.round(domain.weight * 100)}%
          </span>
        </div>

        {hasProgress ? (
          <div className="space-y-2 mt-3">
            <div className="flex items-center gap-3 text-xs text-mutedink">
              <span className="tabular-nums">{progress.attempts} questions</span>
              <span className="text-faint">·</span>
              <span className="tabular-nums text-read">{progress.success_rate}% success</span>
              <span className="text-faint">·</span>
              <span className="tabular-nums">{progress.avg_score}/10 avg</span>
            </div>
            <div className="w-full bg-line-subtle rounded-full h-1 overflow-hidden">
              <div
                className="bg-accent h-1 transition-all duration-500"
                style={{ width: `${successPct}%` }}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-faint mt-2">No progress yet</p>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <Button to={`/course/${courseId}/quiz/${domain.key}`} variant="primary" size="sm" className="flex-1">
          <Play className="w-3.5 h-3.5" /> Quiz
        </Button>
        <Button to={`/course/${courseId}/content/${domain.key}`} variant="secondary" size="sm" aria-label="Content">
          <BookOpen className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}
